// src/components/AppLauncher/Navigator.js

import React, { useRef, useState, useCallback, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Move } from 'lucide-react';

const NAVIGATOR_SIZE = 320;

const Navigator = forwardRef(({
  activeApps,
  windowStates,
  focusedWindowId,
  canvasTransform,
  onViewportChange,
  onVisibilityChange,
  topOffset = 0,
  viewportWidth,
  viewportHeight,
  buttonRef
}, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const navigatorRef = useRef(null);
  const navigatorStateRef = useRef(null);
  const localCanvasTransformRef = useRef(null);

  const [isDraggingViewport, setIsDraggingViewport] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const initialRectRef = useRef(null);

  // Original handleToggle logic
  const handleToggle = useCallback(() => {
    setIsVisible((prev) => {
      const newVisible = !prev;
      if (newVisible) {
        // Compute navigator state ONCE when becoming visible
        const state = computeNavigatorTransform(
          activeApps,
          windowStates,
          canvasTransform,
          viewportWidth,
          viewportHeight
        );
        navigatorStateRef.current = state;

        // Store the current canvasTransform as local
        localCanvasTransformRef.current = { ...canvasTransform };

        onVisibilityChange?.(true);
      } else {
        onVisibilityChange?.(false);
        navigatorStateRef.current = null;
        localCanvasTransformRef.current = null;
      }
      return newVisible;
    });
  }, [activeApps, windowStates, canvasTransform, viewportWidth, viewportHeight, onVisibilityChange]);

  // Expose handleToggle to parent via ref
  useImperativeHandle(ref, () => ({
    handleToggle
  }));

  const navigatorState = navigatorStateRef.current;
  const localCanvasTransform = localCanvasTransformRef.current;

  const viewportRect = useMemo(() => {
    if (!navigatorState || !navigatorState.bounds || !localCanvasTransform) return null;
    return computeViewportRect(
      localCanvasTransform,
      navigatorState.bounds,
      navigatorState.scale,
      navigatorState.offsetX,
      navigatorState.offsetY,
      viewportWidth,
      viewportHeight
    );
  }, [navigatorState, localCanvasTransform, viewportWidth, viewportHeight]);

  const startDrag = useCallback((e) => {
    if (!viewportRect) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingViewport(true);
    initialRectRef.current = viewportRect;
    setDragOffset({ x: 0, y: 0 });
  }, [viewportRect]);

  const onMouseMove = useCallback((e) => {
    if (!isDraggingViewport || !initialRectRef.current) return;
    setDragOffset(prev => ({ x: prev.x + e.movementX, y: prev.y + e.movementY }));
  }, [isDraggingViewport]);

  const onMouseUp = useCallback(() => {
    if (!isDraggingViewport || !navigatorState || !initialRectRef.current) {
      setIsDraggingViewport(false);
      return;
    }

    const { bounds, scale, offsetX, offsetY } = navigatorState;
    const finalX = initialRectRef.current.x + dragOffset.x;
    const finalY = initialRectRef.current.y + dragOffset.y;

    const worldX = (finalX - offsetX) / scale + bounds.minX;
    const worldY = (finalY - offsetY) / scale + bounds.minY;

    onViewportChange({
      x: worldX,
      y: worldY,
      scale: localCanvasTransformRef.current.scale
    });

    localCanvasTransformRef.current = {
      ...localCanvasTransformRef.current,
      translateX: worldX * localCanvasTransformRef.current.scale,
      translateY: worldY * localCanvasTransformRef.current.scale
    };

    setIsDraggingViewport(false);
    initialRectRef.current = null;
    setDragOffset({ x: 0, y: 0 });
  }, [isDraggingViewport, dragOffset, navigatorState, onViewportChange]);

  useEffect(() => {
    if (isDraggingViewport) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    } else {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDraggingViewport, onMouseMove, onMouseUp]);

  useEffect(() => {
    if (!isVisible) return;
    const handleClickOutside = (event) => {
      if (
        navigatorRef.current &&
        !navigatorRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsVisible(false);
        onVisibilityChange?.(false);
        navigatorStateRef.current = null;
        localCanvasTransformRef.current = null;
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVisible, onVisibilityChange, buttonRef]);

  if (!isVisible) return null;

  return (
    <div className="absolute" style={{ top: topOffset, left: 0, zIndex: 50 }}>
      {isVisible && (
        <div
          ref={navigatorRef}
          // Updated background to 50% transparent
          className="relative bg-gray-800/50 border border-gray-700 rounded"
          style={{ width: NAVIGATOR_SIZE, height: NAVIGATOR_SIZE, overflow: 'hidden' }}
        >
          {renderNavigatorContent(activeApps, windowStates, focusedWindowId, navigatorState, viewportRect, isDraggingViewport, dragOffset, initialRectRef, startDrag)}
        </div>
      )}
    </div>
  );
});

function renderNavigatorContent(activeApps, windowStates, focusedWindowId, navigatorState, viewportRect, isDraggingViewport, dragOffset, initialRectRef, startDrag) {
  if (!navigatorState || !navigatorState.bounds || !activeApps.length) {
    return (
      <div className="w-full h-full flex items-center justify-center text-white text-sm bg-gray-800 overflow-hidden">
        No Windows
      </div>
    );
  }

  const { bounds, scale, offsetX, offsetY } = navigatorState;

  let displayedViewportRect = viewportRect;
  if (isDraggingViewport && initialRectRef.current && displayedViewportRect) {
    displayedViewportRect = {
      ...initialRectRef.current,
      x: initialRectRef.current.x + dragOffset.x,
      y: initialRectRef.current.y + dragOffset.y
    };
  }

  return (
    <div className="relative w-full h-full overflow-hidden">
      {activeApps.map((app) => {
        const state = windowStates[app.id];
        if (!state) return null;
        const { x, y, width, height } = transformWindowToNavigator(state, bounds, scale, offsetX, offsetY);
        const isFocused = focusedWindowId === app.id;
        const color = isFocused ? 'bg-green-500' : 'bg-gray-500';
        return (
          <div
            key={app.id}
            className={`absolute opacity-60 ${color}`}
            style={{ left: x, top: y, width, height }}
          />
        );
      })}

      {displayedViewportRect && (
        <div
          className="absolute border-2 border-red-500 bg-red-500/20 cursor-grab"
          style={{
            left: displayedViewportRect.x,
            top: displayedViewportRect.y,
            width: displayedViewportRect.width,
            height: displayedViewportRect.height
          }}
        >
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 p-1 rounded-full bg-white/20 hover:bg-white/30 cursor-move"
            onMouseDown={startDrag}
          >
            <Move size={16} className="text-white" />
          </div>
        </div>
      )}
    </div>
  );
}

function computeNavigatorTransform(activeApps, windowStates, canvasTransform, viewportWidth, viewportHeight) {
  if (!activeApps.length || !viewportWidth || !viewportHeight) {
    return { bounds: null, scale: 1, offsetX: 0, offsetY: 0 };
  }

  let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;

  for (const app of activeApps) {
    const state = windowStates[app.id];
    if (!state?.position) continue;
    const right = state.position.x + (state.size?.width || 800);
    const bottom = state.position.y + (state.size?.height || 600);

    if (state.position.x < minX) minX = state.position.x;
    if (right > maxX) maxX = right;
    if (state.position.y < minY) minY = state.position.y;
    if (bottom > maxY) maxY = bottom;
  }

  const worldX = canvasTransform.translateX / canvasTransform.scale;
  const worldY = canvasTransform.translateY / canvasTransform.scale;
  const worldViewportWidth = viewportWidth / canvasTransform.scale;
  const worldViewportHeight = viewportHeight / canvasTransform.scale;

  if (worldX < minX) minX = worldX;
  if (worldY < minY) minY = worldY;
  if (worldX + worldViewportWidth > maxX) maxX = worldX + worldViewportWidth;
  if (worldY + worldViewportHeight > maxY) maxY = worldY + worldViewportHeight;

  if (minX === Infinity) {
    return { bounds: null, scale: 1, offsetX: 0, offsetY: 0 };
  }

  let width = maxX - minX;
  let height = maxY - minY;

  const marginFactor = 0.2;
  const marginX = width * (marginFactor / 2);
  const marginY = height * (marginFactor / 2);

  minX -= marginX;
  maxX += marginX;
  minY -= marginY;
  maxY += marginY;

  width = maxX - minX;
  height = maxY - minY;

  const scale = Math.min(NAVIGATOR_SIZE / width, NAVIGATOR_SIZE / height);
  const usedWidth = width * scale;
  const usedHeight = height * scale;
  const offsetX = (NAVIGATOR_SIZE - usedWidth) / 2;
  const offsetY = (NAVIGATOR_SIZE - usedHeight) / 2;

  return {
    bounds: { minX, minY, width, height },
    scale,
    offsetX,
    offsetY
  };
}

function computeViewportRect(localCanvasTransform, bounds, scale, offsetX, offsetY, viewportWidth, viewportHeight) {
  if (!bounds || !viewportWidth || !viewportHeight) return null;

  const worldX = localCanvasTransform.translateX / localCanvasTransform.scale;
  const worldY = localCanvasTransform.translateY / localCanvasTransform.scale;
  const worldViewportWidth = viewportWidth / localCanvasTransform.scale;
  const worldViewportHeight = viewportHeight / localCanvasTransform.scale;

  const navX = (worldX - bounds.minX) * scale + offsetX;
  const navY = (worldY - bounds.minY) * scale + offsetY;
  const navW = worldViewportWidth * scale;
  const navH = worldViewportHeight * scale;

  return { x: navX, y: navY, width: navW, height: navH };
}

function transformWindowToNavigator(state, bounds, scale, offsetX, offsetY) {
  const x = (state.position.x - bounds.minX) * scale + offsetX;
  const y = (state.position.y - bounds.minY) * scale + offsetY;
  const width = (state.size?.width || 800) * scale;
  const height = (state.size?.height || 600) * scale;
  return { x, y, width, height };
}

export default Navigator;
